import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, HStack, Skeleton } from '@rtkwlf/fenrir-react';
import { faBullseye } from '@rtkwlf/pro-solid-svg-icons/faBullseye';
import { faChevronRight } from '@rtkwlf/pro-solid-svg-icons/faChevronRight';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { faRotateBack } from '@rtkwlf/pro-solid-svg-icons/faRotateBack';
import { useQuery } from '@tanstack/react-query';

import { useCipherContextSubscription } from '@apis/cipher/use-cipher-context';
import {
  useGetOrganizationOptions,
  useGetOrganizationSecurityFocusOptions,
} from '@apis/track-public/query-options';

import { SecurityFocusTimeline } from '../$focusID/components/security-focus-timeline';
import { LinkButton } from '../../../components/link-button';
import {
  PagePanel,
  PagePanelHeader,
  PagePanelTitle,
} from '../../../components/page/page';
import { WidgetEmpty } from '../../../components/widget/widget-empty';
import { WidgetError } from '../../../components/widget/widget-error';
import { routes } from '../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';

import styles from './active-security-focus-timeline.module.css';

export const ActiveSecurityFocusTimeline: React.FunctionComponent = () => {
  /**
   * Subscribe to being able to set Cipher context
   */
  const setCipherContext = useCipherContextSubscription();

  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  const {
    data: organization,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization,
    refetch: refetchOrganization,
  } = useQuery(useGetOrganizationOptions());

  const {
    data: focus,
    isLoading: isLoadingFocus,
    isError: isErrorFocus,
    refetch: refetchFocus,
  } = useQuery({
    ...useGetOrganizationSecurityFocusOptions({
      focusID: organization?.activeSecurityFocus?.id ?? '',
    }),
    enabled: organization?.activeSecurityFocus != null,
  });

  const handleRefetch = React.useCallback(() => {
    if (isErrorOrganization) {
      void refetchOrganization();
      void refetchFocus();
    } else {
      void refetchFocus();
    }
  }, [isErrorOrganization, refetchFocus, refetchOrganization]);

  if (isLoadingOrganization || isLoadingFocus) {
    return (
      <PagePanel data-testid="active-security-focus-timeline-loading">
        <Skeleton width="full" height="10rem" margin="zero" />
      </PagePanel>
    );
  }

  if (isErrorOrganization || isErrorFocus) {
    return (
      <PagePanel>
        <WidgetError
          actionIcon={<FontAwesomeIcon icon={faRotateBack} size="sm" />}
          actionText={t('common.retry')}
          heading={t(
            'securityFocuses.activeSecurityFocusTimeline.error.heading',
          )}
          description={t('common.clickToRetry')}
          icon={<FontAwesomeIcon icon={faExclamationTriangle} size="2x" />}
          onActionClick={() => handleRefetch()}
        />
      </PagePanel>
    );
  }

  if (organization && organization.activeSecurityFocus == null) {
    return (
      <PagePanel>
        <WidgetEmpty
          icon={<FontAwesomeIcon icon={faBullseye} size="2x" />}
          heading={t(
            'securityFocuses.activeSecurityFocusTimeline.noActiveSecurityFocus.heading',
          )}
          description={t(
            'securityFocuses.activeSecurityFocusTimeline.noActiveSecurityFocus.description',
          )}
        />
      </PagePanel>
    );
  }

  /**
   * Set Cipher context to the current focus
   */
  if (focus) {
    setCipherContext({
      contextContent: JSON.stringify(focus),
      contextType: 'security-focus',
      contextId: focus?.id,
    });
  }

  return (
    focus && (
      <PagePanel className={styles['active-security-focus']}>
        <PagePanelHeader className={styles['header']}>
          <div className={styles['title-container']}>
            <PagePanelTitle>{focus.name}</PagePanelTitle>
            <Badge.Root appearance="info">
              <Badge.Text>{t('securityFocuses.focusStatus.active')}</Badge.Text>
            </Badge.Root>
          </div>
          <LinkButton
            variant="tertiary"
            href={routes.securityFocuses.focusID.index({
              focusID: focus.id,
            })}
          >
            <HStack gap="xsmall">
              <span>
                {t('securityFocuses.activeSecurityFocusTimeline.viewDetails')}
              </span>
              <FontAwesomeIcon
                icon={faChevronRight}
                size="sm"
                style={{ marginTop: '2px' }}
              />
            </HStack>
          </LinkButton>
        </PagePanelHeader>
        <SecurityFocusTimeline focus={focus} />
      </PagePanel>
    )
  );
};
